import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  IconButton,
  Popper,
  Paper,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  TextField,
  ClickAwayListener,
  Divider,
  MenuItem,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import FilterListOffRoundedIcon from "@mui/icons-material/FilterListOffRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardRounded";
import { useTheme } from "@mui/material";
import dayjs from "dayjs";

const ColumnFilter = ({
  data = [],
  field,
  onFilterChange,
  selectedValues = [],
  valueGetter,
  onSortChange,
  isDateField = false,
  openFilter = null,
  setOpenFilter = null,
  currentSort = null,
  enableCustomNumericFilter = false,
}) => {
  const theme = useTheme();
  const [localAnchorEl, setLocalAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedValues, setLocalSelectedValues] =
    useState(selectedValues);
  const buttonRef = useRef(null);

  // Use global state if provided, otherwise use local state
  const isOpen =
    openFilter !== undefined ? openFilter === field : Boolean(localAnchorEl);
  const anchorEl = isOpen ? buttonRef.current : null;

  // Get unique values from the data
  const uniqueValues = React.useMemo(() => {
    const values = data.map((row) => {
      if (valueGetter) {
        return valueGetter(null, row);
      }
      return row[field] || "N/A";
    });

    let unique = [...new Set(values)];

    if (isDateField) {
      // Group dates by year and month
      const dateGroups = {};
      unique.forEach((value) => {
        if (value === "N/A") {
          dateGroups["(blanks)"] = "(blanks)";
        } else {
          const date = dayjs(value);
          if (date.isValid()) {
            const year = date.year();
            const month = date.format("MMMM");
            const yearMonth = `${year} - ${month}`;
            dateGroups[yearMonth] = value;
          }
        }
      });
      return Object.keys(dateGroups).sort((a, b) => {
        if (a === "(blanks)") return 1;
        if (b === "(blanks)") return -1;
        return a.localeCompare(b);
      });
    } else {
      // Replace N/A with (blanks) for non-date fields
      unique = unique.map((value) => (value === "N/A" ? "(blanks)" : value));
      return unique.sort((a, b) => {
        if (a === "(blanks)") return 1;
        if (b === "(blanks)") return -1;
        return String(a).localeCompare(String(b));
      });
    }
  }, [data, field, valueGetter, isDateField]);

  // Helper function to extract numeric value from a string
  const extractNumericValue = (value) => {
    if (typeof value === "number") return value;
    const numMatch = String(value).match(/[-+]?\d+\.?\d*/);
    return numMatch ? parseFloat(numMatch[0]) : NaN;
  };

  // Helper function to check if search term is a numeric comparison
  const parseNumericComparison = useCallback((searchTerm) => {
    if (!enableCustomNumericFilter || !searchTerm.trim()) return null;

    const match = searchTerm.trim().match(/^(>=?|<=?)\s*(\d+\.?\d*)$/);
    if (!match) return null;

    return {
      operator: match[1],
      value: parseFloat(match[2]),
    };
  }, [enableCustomNumericFilter]);

  // Filter values based on search term
  const filteredValues = uniqueValues.filter((value) => {
    const comparison = parseNumericComparison(searchTerm);

    if (comparison) {
      // Numeric comparison mode
      const numericValue = extractNumericValue(value);
      if (isNaN(numericValue)) return false;

      switch (comparison.operator) {
        case ">":
          return numericValue > comparison.value;
        case ">=":
          return numericValue >= comparison.value;
        case "<":
          return numericValue < comparison.value;
        case "<=":
          return numericValue <= comparison.value;
        default:
          return false;
      }
    } else {
      // Standard text search
      return String(value).toLowerCase().includes(searchTerm.toLowerCase());
    }
  });

  useEffect(() => {
    if (selectedValues.length === 0) {
      // If no filter is active, show all items as selected
      setLocalSelectedValues([...uniqueValues]);
    } else {
      // Convert actual values to display values
      const displayValues = selectedValues.map((value) => {
        if (value === "N/A") return "(blanks)";
        if (isDateField && value !== "N/A") {
          const date = dayjs(value);
          if (date.isValid()) {
            const year = date.year();
            const month = date.format("MMMM");
            return `${year} - ${month}`;
          }
        }
        return value;
      });
      setLocalSelectedValues([...new Set(displayValues)]);
    }
  }, [selectedValues, isDateField, uniqueValues]);

  // Auto-select filtered values when using numeric comparison
  useEffect(() => {
    const comparison = parseNumericComparison(searchTerm);
    if (comparison && filteredValues.length > 0) {
      setLocalSelectedValues([...filteredValues]);
    }
  }, [searchTerm, filteredValues, parseNumericComparison]);

  const open = isOpen;

  const handleClick = () => {
    if (setOpenFilter) {
      // Use global state - always close any open filter first, then open this one if it wasn't already open
      if (openFilter === field) {
        setOpenFilter(null);
      } else {
        setOpenFilter(field);
      }
    } else {
      // Use local state (backward compatibility)
      setLocalAnchorEl(localAnchorEl ? null : buttonRef.current);
    }
  };

  const handleClose = useCallback(() => {
    if (setOpenFilter) {
      // Only close if this filter is currently open
      if (openFilter === field) {
        setOpenFilter(null);
      }
    } else {
      setLocalAnchorEl(null);
    }
    setSearchTerm("");
  }, [field, openFilter, setOpenFilter]);

  // ESC key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleClose]);

  const handleSelectAll = () => {
    setLocalSelectedValues([...uniqueValues]);
  };

  const handleSelectNone = () => {
    setLocalSelectedValues([]);
  };

  const handleValueToggle = (value) => {
    setLocalSelectedValues((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleApply = () => {
    let finalSelectedValues = localSelectedValues;

    // If there's a numeric comparison search term, use all filtered values
    const comparison = parseNumericComparison(searchTerm);
    if (comparison) {
      finalSelectedValues = filteredValues;
    } else if (searchTerm) {
      // If there's a regular search term, only keep values that match the search
      finalSelectedValues = localSelectedValues.filter((v) =>
        filteredValues.includes(v),
      );
    }

    // Convert back from display values to actual values
    const actualValues = finalSelectedValues
      .map((value) => {
        if (value === "(blanks)") return "N/A";
        if (isDateField && value.includes(" - ")) {
          // For date groups, we need to find all actual date values that belong to this group
          const [year, month] = value.split(" - ");
          return data
            .map((row) => (valueGetter ? valueGetter(null, row) : row[field]))
            .filter((dateValue) => {
              if (dateValue === "N/A") return false;
              const date = dayjs(dateValue);
              if (!date.isValid()) return false;
              const dateYear = date.year();
              const dateMonth = date.format("MMMM");
              return dateYear.toString() === year && dateMonth === month;
            });
        }
        return value;
      })
      .flat();

    onFilterChange(actualValues);
    handleClose();
  };

  const handleCancel = () => {
    setLocalSelectedValues(selectedValues);
    handleClose();
  };

  // Use explicit selection to detect active filters; data may shrink after other filters
  const hasActiveFilter = selectedValues.length > 0;

  const handleClearFilter = () => {
    onFilterChange([]);
    handleClose();
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
        size="small"
        sx={{
          color: hasActiveFilter
            ? theme.palette.primary.main
            : theme.palette.primary.text2,
          opacity: hasActiveFilter ? 1 : 0,
          transition: "opacity 0.2s",
          ".MuiDataGrid-columnHeader:hover &": {
            opacity: 1,
          },
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {hasActiveFilter ? (
          <FilterListOffRoundedIcon fontSize="small" />
        ) : (
          <FilterListRoundedIcon fontSize="small" />
        )}
      </IconButton>
      {/* Backdrop */}
      {open &&
        createPortal(
          <Box
            onClick={handleClose}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "transparent",
              cursor: "default",
              zIndex: 9998,
            }}
          />,
          document.body,
        )}

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 9999 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              width: 300,
              maxHeight: 400,
              py: 1.25,
              px: 1.5,
              bgcolor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Clear Filter Button */}
            {hasActiveFilter && (
              <>
                <Button
                  onClick={handleClearFilter}
                  size="small"
                  variant="text"
                  color="primary"
                  startIcon={<ClearIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Clear Filter
                </Button>
                <Divider sx={{ mb: 1 }} />
              </>
            )}

            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder={
                enableCustomNumericFilter
                  ? "Search or use >, <, >=, <="
                  : "Search..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  filteredValues.length > 0 &&
                  localSelectedValues.length > 0
                ) {
                  handleApply();
                }
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ mr: 1, color: theme.palette.primary.text2 }}
                  />
                ),
              }}
            />

            {onSortChange && (
              <>
                <Box sx={{ display: "flex", mt: 0.5 }}>
                  <MenuItem
                    sx={{
                      flex: 1,
                      justifyContent: "center",
                      borderRadius: "6px",
                      backgroundColor:
                        currentSort === "asc"
                          ? theme.palette.background.default
                          : "transparent",
                    }}
                    onClick={() => {
                      if (currentSort === "asc") {
                        onSortChange(field, null);
                      } else {
                        onSortChange(field, "asc");
                      }
                      handleClose();
                    }}
                  >
                    <ArrowUpwardIcon
                      sx={{
                        mr: 1,
                        fontSize: "small",
                        color:
                          currentSort === "asc"
                            ? theme.palette.primary.main
                            : theme.palette.primary.text2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={currentSort === "asc" ? "bold" : "normal"}
                      color={
                        currentSort === "asc"
                          ? theme.palette.primary.main
                          : theme.palette.primary.text2
                      }
                    >
                      Sort A-Z
                    </Typography>
                  </MenuItem>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <MenuItem
                    sx={{
                      flex: 1,
                      justifyContent: "center",
                      backgroundColor:
                        currentSort === "desc"
                          ? theme.palette.background.default
                          : "transparent",
                      borderRadius: "6px",
                    }}
                    onClick={() => {
                      if (currentSort === "desc") {
                        onSortChange(field, null);
                      } else {
                        onSortChange(field, "desc");
                      }
                      handleClose();
                    }}
                  >
                    <ArrowDownwardIcon
                      sx={{
                        mr: 1,
                        fontSize: "small",
                        color:
                          currentSort === "desc"
                            ? theme.palette.primary.main
                            : theme.palette.primary.text2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={currentSort === "desc" ? "bold" : "normal"}
                      color={
                        currentSort === "desc"
                          ? theme.palette.primary.main
                          : theme.palette.primary.text2
                      }
                    >
                      Sort Z-A
                    </Typography>
                  </MenuItem>
                </Box>
                <Divider sx={{ mt: 1 }} />
              </>
            )}

            {/* Values List */}
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                flex: 1,
                minHeight: 0,
                mt: 0.5,
              }}
            >
              {/* Select All Item */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localSelectedValues.length === uniqueValues.length}
                    indeterminate={
                      localSelectedValues.length > 0 &&
                      localSelectedValues.length < uniqueValues.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSelectAll();
                      } else {
                        handleSelectNone();
                      }
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                  >
                    Select All
                  </Typography>
                }
                sx={{
                  display: "flex",
                  width: "100%",
                  m: 0,
                  mb: 0.5,
                  "& .MuiFormControlLabel-label": {
                    flex: 1,
                  },
                }}
              />
              <Divider sx={{ mb: 0.5 }} />
              {filteredValues.map((value) => (
                <FormControlLabel
                  key={value}
                  control={
                    <Checkbox
                      checked={localSelectedValues.includes(value)}
                      onChange={() => handleValueToggle(value)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {String(value)}
                    </Typography>
                  }
                  sx={{
                    display: "flex",
                    width: "100%",
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      flex: 1,
                    },
                  }}
                />
              ))}
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 0.75,
                flexShrink: 0,
              }}
            >
              <Button onClick={handleCancel} size="small" variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                size="small"
                variant="contained"
                disabled={localSelectedValues.length === 0}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ColumnFilter;
